import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';
import {
  CogIcon,
  PhotoIcon,
  PaintBrushIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon
} from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';

/* Odoo-style settings shell: a compact top-left title, a plain-text
   sidebar (no icons, a soft tint highlight on the active tab) and
   borderless content sections separated by thin rules -- matching the
   reference screenshot's layout. The 5 real tabs/fields underneath are
   unchanged (General/Appearance/Localization/Notifications/System, backed
   by settingsService/ApplicationSettings): this is a restyle, not a swap
   to the screenshot's different, not-yet-built settings (SMS channel,
   weighted categories, two-factor auth, etc.). */

// Shared toggle row: bold label + muted description on the left, a switch
// on the right. Used in a responsive grid so several toggles line up the
// way the reference groups them (2-3 per row) instead of one full-width
// bordered box per setting.
const ToggleRow = ({ label, description, checked, onChange, accent = 'var(--accent)' }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <div className="text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>{label}</div>
      {description && (
        <div className="mt-0.5 text-[12px] leading-snug" style={{ color: 'var(--muted)' }}>{description}</div>
      )}
    </div>
    <label className="relative inline-flex flex-shrink-0 cursor-pointer items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <div
        className="h-6 w-11 rounded-full transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-['']"
        style={{ background: checked ? accent : 'var(--track)' }}
      >
        <div
          className="h-5 w-5 rounded-full bg-white shadow transition-transform"
          style={{ transform: checked ? 'translateX(20px) translateY(2px) translateX(-2px)' : 'translateY(2px) translateX(2px)' }}
        />
      </div>
    </label>
  </div>
);

const SectionHeader = ({ title }) => (
  <h3
    className="mb-4 pb-2 text-[13px] font-semibold uppercase tracking-wide"
    style={{ color: 'var(--body-2)', borderBottom: '1px solid var(--tint-line)' }}
  >
    {title}
  </h3>
);

const ToggleGrid = ({ children }) => (
  <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-3">{children}</div>
);

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [themeOptions, setThemeOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  useEffect(() => {
    loadSettings();
    loadOptions();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await settingsService.getApplicationSettings();
      setSettings(data);
    } catch (error) {
      showMessage('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [themes, languages, timezones] = await Promise.all([
        settingsService.getThemeOptions(),
        settingsService.getLanguageOptions(),
        settingsService.getTimezoneOptions()
      ]);
      setThemeOptions(themes);
      setLanguageOptions(languages);
      setTimezoneOptions(timezones);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      await settingsService.updateApplicationSettings(settings);
      showMessage('Settings saved successfully!', 'success');
    } catch (error) {
      showMessage(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await settingsService.uploadLogo(file);
      setSettings(prev => ({ ...prev, logoUrl: result.fileUrl }));
      showMessage('Logo uploaded successfully!', 'success');
    } catch (error) {
      showMessage(error.message, 'error');
    }
  };

  const handleFaviconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await settingsService.uploadFavicon(file);
      setSettings(prev => ({ ...prev, faviconUrl: result.fileUrl }));
      showMessage('Favicon uploaded successfully!', 'success');
    } catch (error) {
      showMessage(error.message, 'error');
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) return;

    try {
      await settingsService.resetToDefaults();
      await loadSettings();
      showMessage('Settings reset to defaults successfully!', 'success');
    } catch (error) {
      showMessage(error.message, 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const getThemeIcon = (theme) => {
    switch (theme) {
      case 'light': return <SunIcon className="w-4 h-4" />;
      case 'dark': return <MoonIcon className="w-4 h-4" />;
      case 'auto': return <ComputerDesktopIcon className="w-4 h-4" />;
      default: return <PaintBrushIcon className="w-4 h-4" />;
    }
  };

  const tabs = [
    { id: 'general', name: 'General' },
    { id: 'appearance', name: 'Appearance' },
    { id: 'localization', name: 'Localization' },
    { id: 'notifications', name: 'Notifications' },
    { id: 'system', name: 'System' },
  ];

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)]"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center">
          <CogIcon className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--faint)' }} />
          <p style={{ color: 'var(--muted)' }}>Failed to load settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full" style={{ background: 'var(--surface)' }}>
      {/* Compact title bar */}
      <div className="border-b px-6 py-4" style={{ borderColor: 'var(--tint-line)' }}>
        <h1 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>Settings</h1>
      </div>

      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-6 mt-4 rounded-lg p-3 text-sm"
          style={message.type === 'success'
            ? { background: 'var(--b-green-bg)', color: 'var(--b-green-fg)' }
            : { background: 'var(--b-red-bg)', color: 'var(--b-red-fg)' }}
        >
          <div className="flex items-center">
            <CheckCircleIcon className="mr-2 h-5 w-5 flex-shrink-0" />
            {message.text}
          </div>
        </motion.div>
      )}

      <div className="flex w-full flex-col lg:flex-row">
        {/* Sidebar Navigation */}
        <div
          className="w-full flex-shrink-0 border-b p-4 lg:w-56 lg:border-b-0 lg:border-r"
          style={{ borderColor: 'var(--tint-line)' }}
        >
          <nav className="space-y-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="block w-full rounded-md px-3 py-2 text-left text-[13px] font-medium transition-colors"
                style={activeTab === tab.id
                  ? { background: 'var(--tint)', color: 'var(--accent-strong)' }
                  : { color: 'var(--body)' }}
                onMouseEnter={(e) => { if (activeTab !== tab.id) e.currentTarget.style.background = 'var(--hover)'; }}
                onMouseLeave={(e) => { if (activeTab !== tab.id) e.currentTarget.style.background = 'transparent'; }}
              >
                {tab.name}
              </button>
            ))}
          </nav>

          <div className="mt-6 border-t pt-4" style={{ borderColor: 'var(--tint-line)' }}>
            <button
              onClick={handleResetToDefaults}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-[12px] transition-colors"
              style={{ color: 'var(--neg)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--b-red-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              Reset to defaults
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <AnimatePresence mode="wait">
            {activeTab === 'general' && (
              <GeneralSettings
                key="general"
                settings={settings}
                onChange={setSettings}
                onLogoUpload={handleLogoUpload}
                onFaviconUpload={handleFaviconUpload}
              />
            )}
            {activeTab === 'appearance' && (
              <AppearanceSettings
                key="appearance"
                settings={settings}
                onChange={setSettings}
                themeOptions={themeOptions}
                getThemeIcon={getThemeIcon}
              />
            )}
            {activeTab === 'localization' && (
              <LocalizationSettings
                key="localization"
                settings={settings}
                onChange={setSettings}
                languageOptions={languageOptions}
                timezoneOptions={timezoneOptions}
              />
            )}
            {activeTab === 'notifications' && (
              <NotificationSettings
                key="notifications"
                settings={settings}
                onChange={setSettings}
              />
            )}
            {activeTab === 'system' && (
              <SystemSettings
                key="system"
                settings={settings}
                onChange={setSettings}
              />
            )}
          </AnimatePresence>

          {/* Save Button */}
          <div className="mt-8 border-t pt-5" style={{ borderColor: 'var(--tint-line)' }}>
            <div className="flex items-center justify-between">
              <div className="text-[12px]" style={{ color: 'var(--faint)' }}>
                Changes will take effect immediately
              </div>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg px-6 py-2.5 text-[13px] font-medium transition-all disabled:opacity-50"
                style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
                onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = 'var(--accent-strong)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
              >
                {saving ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// General Settings Component
const GeneralSettings = ({ settings, onChange, onLogoUpload, onFaviconUpload }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <SectionHeader title="Branding" />

      <div className="space-y-3">
        <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>
          Application Name
        </label>
        <input
          type="text"
          value={settings.applicationName}
          onChange={(e) => onChange({ ...settings, applicationName: e.target.value })}
          className="w-full max-w-md rounded-lg border px-3 py-2 text-[13px] outline-none transition-colors focus:ring-2"
          style={{ borderColor: 'var(--line-3)', '--tw-ring-color': 'var(--accent)' }}
          placeholder="Enter your application name"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>
            Application Logo
          </label>
          <div
            className="rounded-lg border border-dashed p-5 text-center transition-colors"
            style={{ borderColor: 'var(--line)' }}
          >
            <div className="flex flex-col items-center">
              {settings.logoUrl ? (
                <img src={settings.logoUrl} alt="Application Logo" className="mb-3 h-24 w-24 rounded-lg object-contain" />
              ) : (
                <PhotoIcon className="mb-3 h-12 w-12" style={{ color: 'var(--faint)' }} />
              )}
              <input type="file" onChange={onLogoUpload} accept=".png,.jpg,.jpeg,.svg,.ico" className="hidden" id="logo-upload" />
              <label
                htmlFor="logo-upload"
                className="inline-block cursor-pointer rounded-lg px-4 py-2 text-[12px] font-medium transition-colors"
                style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
              >
                Upload Logo
              </label>
              <p className="mt-2 text-[11px]" style={{ color: 'var(--faint)' }}>PNG, JPG, SVG, ICO &middot; Max 5MB</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>
            Favicon
          </label>
          <div
            className="rounded-lg border border-dashed p-5 text-center transition-colors"
            style={{ borderColor: 'var(--line)' }}
          >
            <div className="flex flex-col items-center">
              {settings.faviconUrl ? (
                <img src={settings.faviconUrl} alt="Favicon" className="mb-3 h-12 w-12 rounded object-contain" />
              ) : (
                <PhotoIcon className="mb-3 h-10 w-10" style={{ color: 'var(--faint)' }} />
              )}
              <input type="file" onChange={onFaviconUpload} accept=".ico,.png,.svg" className="hidden" id="favicon-upload" />
              <label
                htmlFor="favicon-upload"
                className="inline-block cursor-pointer rounded-lg px-4 py-2 text-[12px] font-medium transition-colors"
                style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
              >
                Upload Favicon
              </label>
              <p className="mt-2 text-[11px]" style={{ color: 'var(--faint)' }}>ICO, PNG, SVG &middot; Max 1MB</p>
            </div>
          </div>
        </div>
      </div>

      <SectionHeader title="Colors" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Primary Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={settings.primaryColor}
              onChange={(e) => onChange({ ...settings, primaryColor: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded-lg border"
              style={{ borderColor: 'var(--line-3)' }}
            />
            <input
              type="text"
              value={settings.primaryColor}
              onChange={(e) => onChange({ ...settings, primaryColor: e.target.value })}
              className="flex-1 rounded-lg border px-3 py-2 font-mono text-[13px] outline-none"
              style={{ borderColor: 'var(--line-3)' }}
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Secondary Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={settings.secondaryColor}
              onChange={(e) => onChange({ ...settings, secondaryColor: e.target.value })}
              className="h-10 w-14 cursor-pointer rounded-lg border"
              style={{ borderColor: 'var(--line-3)' }}
            />
            <input
              type="text"
              value={settings.secondaryColor}
              onChange={(e) => onChange({ ...settings, secondaryColor: e.target.value })}
              className="flex-1 rounded-lg border px-3 py-2 font-mono text-[13px] outline-none"
              style={{ borderColor: 'var(--line-3)' }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Appearance Settings Component
const AppearanceSettings = ({ settings, onChange, themeOptions, getThemeIcon }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <SectionHeader title="Appearance" />

      <div className="flex flex-wrap items-center gap-4">
        <span className="text-[13px] font-medium" style={{ color: 'var(--body-2)' }}>Interface theme</span>
        <div className="inline-flex overflow-hidden rounded-lg border" style={{ borderColor: 'var(--line-3)' }}>
          {themeOptions.map((theme, index) => (
            <button
              key={theme.value}
              onClick={() => onChange({ ...settings, defaultTheme: theme.value })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium transition-colors"
              style={{
                background: settings.defaultTheme === theme.value ? 'var(--tint)' : 'var(--surface)',
                color: settings.defaultTheme === theme.value ? 'var(--accent-strong)' : 'var(--body)',
                borderLeft: index === 0 ? 'none' : '1px solid var(--line-3)'
              }}
            >
              {getThemeIcon(theme.value)}
              {theme.label}
            </button>
          ))}
        </div>
        <span className="text-[12px]" style={{ color: 'var(--faint)' }}>
          Applies across the whole app.
        </span>
      </div>

      <SectionHeader title="Display" />

      <div className="max-w-xs space-y-3">
        <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Items Per Page</label>
        <select
          value={settings.itemsPerPage}
          onChange={(e) => onChange({ ...settings, itemsPerPage: parseInt(e.target.value) })}
          className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none"
          style={{ borderColor: 'var(--line-3)' }}
        >
          <option value="5">5 items</option>
          <option value="10">10 items</option>
          <option value="25">25 items</option>
          <option value="50">50 items</option>
          <option value="100">100 items</option>
        </select>
      </div>
    </motion.div>
  );
};

// Localization Settings Component
const LocalizationSettings = ({ settings, onChange, languageOptions, timezoneOptions }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <SectionHeader title="Language & region" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Language</label>
          <select
            value={settings.language}
            onChange={(e) => onChange({ ...settings, language: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none"
            style={{ borderColor: 'var(--line-3)' }}
          >
            {languageOptions.map((lang) => (
              <option key={lang.code} value={lang.code}>{lang.name} ({lang.nativeName})</option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Timezone</label>
          <select
            value={settings.timezone}
            onChange={(e) => onChange({ ...settings, timezone: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none"
            style={{ borderColor: 'var(--line-3)' }}
          >
            {timezoneOptions.map((tz) => (
              <option key={tz.id} value={tz.id}>{tz.name} ({tz.offset})</option>
            ))}
          </select>
        </div>
      </div>

      <SectionHeader title="Formats" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Date Format</label>
          <select
            value={settings.dateFormat}
            onChange={(e) => onChange({ ...settings, dateFormat: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none"
            style={{ borderColor: 'var(--line-3)' }}
          >
            <option value="MM/dd/yyyy">MM/DD/YYYY (US)</option>
            <option value="dd/MM/yyyy">DD/MM/YYYY (EU)</option>
            <option value="yyyy-MM-dd">YYYY-MM-DD (ISO)</option>
            <option value="MMM dd, yyyy">MMM DD, YYYY</option>
          </select>
        </div>

        <div className="space-y-3">
          <label className="block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Time Format</label>
          <select
            value={settings.timeFormat}
            onChange={(e) => onChange({ ...settings, timeFormat: e.target.value })}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none"
            style={{ borderColor: 'var(--line-3)' }}
          >
            <option value="12h">12-hour (AM/PM)</option>
            <option value="24h">24-hour</option>
          </select>
        </div>
      </div>
    </motion.div>
  );
};

// Notification Settings Component
const NotificationSettings = ({ settings, onChange }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <SectionHeader title="Notifications" />

      <ToggleGrid>
        <ToggleRow
          label="Email Notifications"
          description="Receive important updates via email."
          checked={settings.enableEmailNotifications}
          onChange={(checked) => onChange({ ...settings, enableEmailNotifications: checked })}
        />
        <ToggleRow
          label="Push Notifications"
          description="Receive browser push notifications."
          checked={settings.enablePushNotifications}
          onChange={(checked) => onChange({ ...settings, enablePushNotifications: checked })}
        />
      </ToggleGrid>
    </motion.div>
  );
};

// System Settings Component
const SystemSettings = ({ settings, onChange }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <SectionHeader title="System" />

      <ToggleGrid>
        <ToggleRow
          label="Maintenance Mode"
          description="Put the application in maintenance mode."
          checked={settings.maintenanceMode}
          onChange={(checked) => onChange({ ...settings, maintenanceMode: checked })}
          accent="#c07a3a"
        />
        <ToggleRow
          label="Allow User Registrations"
          description="Allow new users to register accounts."
          checked={settings.allowRegistrations}
          onChange={(checked) => onChange({ ...settings, allowRegistrations: checked })}
        />
      </ToggleGrid>

      <SystemInfo />
    </motion.div>
  );
};

// System Info Component
const SystemInfo = () => {
  const [systemInfo, setSystemInfo] = useState(null);

  useEffect(() => {
    loadSystemInfo();
  }, []);

  const loadSystemInfo = async () => {
    try {
      const info = await settingsService.getSystemInfo();
      setSystemInfo(info);
    } catch (error) {
      console.error('Error loading system info:', error);
    }
  };

  if (!systemInfo) return null;

  return (
    <div className="pt-2">
      <SectionHeader title="System Information" />
      <div className="grid grid-cols-1 gap-x-8 gap-y-2 text-[12px] md:grid-cols-2">
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Version:</span>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>{systemInfo.version}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Environment:</span>
          <span className="font-medium capitalize" style={{ color: 'var(--ink)' }}>{systemInfo.environment}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Server Time:</span>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>{new Date(systemInfo.serverTimeLocal).toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Timezone:</span>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>{systemInfo.timezone}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Memory Usage:</span>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>{systemInfo.memoryUsage} MB</span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: 'var(--muted)' }}>Database:</span>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>{systemInfo.databaseProvider}</span>
        </div>
      </div>
    </div>
  );
};

export default Settings;
